import { Grid } from "@mui/material";
import moment from "moment";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../framework/Button";
import Checkbox from "../../framework/Checkbox";
import ChipInputSelect from "../../framework/ChipInputSelect";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { displayToast } from "../../framework/toast/toastUtils";
import { fetchBranches } from "../../services/branch-service";
import { fetchRoles } from "../../services/roles-service";
import {
  getUserById,
  isUserIdAvailable,
  saveUser,
} from "../../services/user-service";
import {
  formStateHandler,
  getISODateTime,
  isEmpty,
  isValidName,
} from "../../utils/utils";
import classes from "./AddUser.module.css";
import { statusOptions, transformPayload } from "./UserReportsConstants";
import DatePicker from "../../framework/date/DatePicker";
import dayjs from "dayjs";

const AddUsers = ({ id, onSaveUser = () => {}, onEscape = () => {} }) => {
  const [formState, setFormState] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const [role, setRole] = useState(null);
  const [branch, setBranch] = useState([]);
  const [status, setStatus] = useState(statusOptions[0]);

  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [designationOption, setDesignationOption] = useState([]);
  const [joiningDate, setJoiningDate] = useState(null);
  const withPhoneCap = ({ value }) => value.length <= 10;

  const usernameRef = useRef("");
  const firstnameRef = useRef("");
  const lastnameRef = useRef("");
  const phoneNumberRef = useRef("");
  const emailRef = useRef("");
  const passwordRef = useRef("");
  const passPhraseRef = useRef("");
  const ttlRef = useRef("");
  const resetPasswordInDaysRef = useRef("");
  const roleRef = useRef(null);
  const saveUserRef = useRef();
  const branchRef = useRef("");
  const statusRef = useRef("");
  const addressRef = useRef("");
  const designationRef = useRef("");
  const joiningDateRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const deactivateDate = moment(formState.ttl, "DD/MM/YYYY");

  const validateForm = useCallback(
    async (fieldName, data) => {
      if (fieldName === "userId" || !fieldName) {
        if (
          (data && !data?.userId?.length) ||
          !isValidName(data?.userId, true, false, false)
        ) {
          displayToast({
            type: "error",
            title:
              "Please enter valid EmployeeId. Only alphabets and numbers are allowed in the field",
          });
          setErrorStates((prev) => {
            return { ...prev, userId: true };
          });
          if (!fieldName) usernameRef.current.focus({ preventScroll: true });
          return false;
        }
        if (!id && !fieldName) {
          const r = await isUserIdAvailable(`userId=${data.userId}`);
          if (r) {
            displayToast({
              type: "error",
              title: "The employee id already exist.",
            });
            setErrorStates((prev) => {
              return { ...prev, userId: true };
            });
            if (!fieldName) usernameRef.current.focus({ preventScroll: true });
            return false;
          }
        }
      }
      if (fieldName === "firstName" || !fieldName) {
        if (!data?.firstName) {
          firstnameRef.current?.focus();
          return displayToast({
            type: "error",
            title: "Please enter first name",
          });
        }
      }
      if (fieldName === "password" || !fieldName) {
        if (data && !data?.password?.length) {
          displayToast({
            type: "error",
            title: "Please enter password",
            autoHideDuration: 10000,
          });
          setErrorStates((prev) => {
            return { ...prev, password: true };
          });
          if (!fieldName) passwordRef.current.focus({ preventScroll: true });
          return false;
        }
        const regex =
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$#!%*?&]{8,}$/;
        if (!regex.test(data.password)) {
          displayToast({
            type: "error",
            title: (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  Password must be alphanumeric.
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Password must be atleast 8 characters in length.
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Password must contain atleast one uppercase letter.
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Password must contain atleast one lowercase letter.
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Password must contain atleast one special character from
                  (@$!%*?&).
                </div>
              </div>
            ),
          });
          setErrorStates((prev) => {
            return { ...prev, password: true };
          });
          if (!fieldName) passwordRef.current.focus({ preventScroll: true });
          return false;
        }
      }
      if (fieldName === "email" || !fieldName) {
        const regex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (data.email && !regex.test(data.email)) {
          displayToast({
            type: "error",
            title: "Please enter a valid email.",
          });
          setErrorStates((prev) => {
            return { ...prev, email: true };
          });
          if (!fieldName) emailRef.current.focus({ preventScroll: true });
          return false;
        }
      }
      if (fieldName === "role" || !fieldName) {
        if (data && isEmpty(data?.role)) {
          displayToast({
            type: "error",
            title: "Please enter role",
          });
          setErrorStates((prev) => {
            return { ...prev, role: true };
          });
          roleRef.current.focus();
          return false;
        }
      }
      if (fieldName === "joiningDate" || !fieldName) {
        if (
          dayjs(data.joiningDate, "DD/MM/YYYY")?.length < 10 ||
          !data.joiningDate
        ) {
          displayToast({
            type: "error",
            title: "Please enter valid Joining Date ",
          });
          joiningDateRef.current.focus({ preventScroll: false });
          setErrorStates((prev) => {
            return { ...prev, joiningDate: true };
          });
          return false;
        }
      }
      if (fieldName === "ttl" || !fieldName) {
        if (dayjs(data.ttl, "DD/MM/YYYY")?.length < 10 || !data.ttl) {
          displayToast({
            type: "error",
            title: "Please enter valid Release Date ",
          });
          ttlRef.current.focus({ preventScroll: false });
          setErrorStates((prev) => {
            return { ...prev, ttl: true };
          });
          return false;
        }
        if (
          dayjs(data.ttl, "DD/MM/YYYY").isBefore(
            dayjs(data.joiningDate, "DD/MM/YYYY")
          )
        ) {
          displayToast({
            type: "error",
            title: "Release Date should be greater than Joining Date ",
          });
          ttlRef.current.focus({ preventScroll: false });
          setErrorStates((prev) => {
            return { ...prev, ttl: true };
          });
          return false;
        }
      }

      if (fieldName === "branch" || !fieldName) {
        if (data && isEmpty(data?.branch)) {
          displayToast({
            type: "error",
            title: "Please enter branch",
          });
          branchRef.current.focus();
          return false;
        }
      }
      return true;
    },
    [id]
  );
  const updateUserHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({ ...formState });

      payload.ttl = payload.ttl ? getISODateTime(payload.ttl) : "";
      payload.joiningDate = payload.joiningDate
        ? getISODateTime(payload.joiningDate)
        : "";
      return saveUser(payload).then((r) => {
        onSaveUser(r);
        return displayToast({
          type: "success",
          title: "User added successfully.",
        });
      });
    },
    [formState, onSaveUser, validateForm]
  );
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveUserRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );
  useEffect(() => {
    fetchRoles(`deleted=false`).then(
      (r) =>
        r.status === "success" &&
        setRoles(
          r.result.map((x) => {
            return {
              ...x,
              id: x._id,
              label: x.role,
            };
          })
        )
    );
  }, []);
  useEffect(() => {
    fetchBranches(`deleted=false`).then((r) => {
      const opts = r.result.rows.map((x) => {
        return {
          ...x,
          id: x._id,
          label: x.code,
        };
      });
      const sortedOpts = opts?.sort((x, y) => (x.region > y.region ? 1 : -1));
      setBranches(sortedOpts);
    });
  }, []);
  useEffect(() => {
    if (id && branches?.length && isEmpty(formState)) {
      getUserById(`id=${id}`).then((r) => {
        let f = { ...r };
        if (r.ttl) {
          f["ttl"] = moment(r.ttl).format("DD/MM/YYYY");
        }
        if (r.joiningDate) {
          f["joiningDate"] = moment(r.joiningDate).format("DD/MM/YYYY");
        }
        if (r.roles?.length && !isEmpty(r.roles[0])) {
          let x = {
            ...r.roles[0],
            id: r.roles[0]?._id,
            label: r.roles[0]?.role,
          };
          setRole(x);
          f["role"] = x;
        }
        if (r.branch && r.branch.length) {
          let updated = [];
          r.branch.forEach((b) => {
            const _b = branches.find((x) => x._id === b);
            if (_b) updated = [...updated, _b];
          });
          setBranch(updated);
          f["branch"] = updated;
        }
        if (r.status) {
          f["status"] = statusOptions.find((e) => r.status === e.id);
          setStatus(f["status"]);
        }
        return setFormState(f);
      });
    }
  }, [branch, branches, formState, id]);
  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);
  return (
    <div>
      <h4 className={classes["user-detail-label"]}>User Details</h4>
      <Grid container spacing={2} sx={{ p: "25px 30px" }}>
        <Grid item xs={4}>
          <TextInput
            id="add_user_username"
            ref={usernameRef}
            name="userId"
            label="Employee Id"
            required={true}
            onFocus={() => setCurrentComponent("add_user_username")}
            autoFocus
            onChange={(e) => {
              handleFormChange("userId", e.target.value.replace(/\s/g, ""));
              if (errorStates?.userId)
                setErrorStates((prev) => {
                  return { ...prev, userId: false };
                });
            }}
            onBlur={() => validateForm("userId", formState)}
            value={formState?.userId || ""}
            maxInputLength={100}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_firstname",
                down: "add_user_phone_number",
                up: "add_user_username",
                left: "add_user_branch",
              }),
            }}
            InputProps={{ readOnly: !!id }}
            error={errorStates?.userId}
            // autoFocus={medicine ? true : false}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_user_firstname"
            ref={firstnameRef}
            name="firstName"
            label="First Name"
            value={formState?.firstName || ""}
            onFocus={() => setCurrentComponent("add_user_firstname")}
            onChange={(e) => {
              // setFormState({ ...formState, firstName: e.target.value });
              handleFormChange("firstName", e.target.value);
              if (errorStates?.firstName)
                setErrorStates((prev) => {
                  return { ...prev, firstName: false };
                });
            }}
            maxInputLength={20}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_lastname",
                down: "add_user_email",
                up: "add_user_firstname",
                left: "add_user_username",
              }),
            }}
            error={errorStates?.firstName}
            required={true}
            // autoFocus={medicine ? true : false}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_user_lastname"
            ref={lastnameRef}
            name="lastName"
            label="Last Name"
            value={formState?.lastName || ""}
            onFocus={() => setCurrentComponent("add_user_lastname")}
            onChange={(e) => {
              handleFormChange("lastName", e.target.value);
              if (errorStates?.lastName)
                setErrorStates((prev) => {
                  return { ...prev, lastName: false };
                });
            }}
            maxInputLength={30}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_phone_number",
                down: "add_user_address",
                up: "add_user_lastname",
                left: "add_user_firstname",
              }),
            }}
            error={errorStates?.lastName}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ p: "0px 30px" }}>
        <Grid item xs={4}>
          <NumberInput
            id="add_user_phone_number"
            ref={phoneNumberRef}
            name="phoneNumber"
            label="Phone Number"
            value={formState?.phoneNumber}
            isAllowed={withPhoneCap}
            allowNegative={false}
            onFocus={() => setCurrentComponent("add_user_phone_number")}
            onValueChange={(values, e) => {
              // setFormState((prev) => {
              //   return { ...prev, phoneNumber: e.target.value };
              // });
              handleFormChange("phoneNumber", values.floatValue);
              if (errorStates?.phoneNumber)
                setErrorStates((prev) => {
                  return { ...prev, phoneNumber: false };
                });
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_email",
                down: "add_user_password",
                up: "add_user_username",
                left: "add_user_lastname",
              }),
            }}
            error={errorStates?.phoneNumber}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_user_email"
            ref={emailRef}
            name="email"
            label="Email"
            value={formState?.email || ""}
            onFocus={() => setCurrentComponent("add_user_email")}
            onChange={(e) => {
              handleFormChange("email", e.target.value);
              if (errorStates?.email)
                setErrorStates((prev) => {
                  return { ...prev, email: false };
                });
            }}
            onBlur={() => validateForm("email", formState)}
            maxInputLength={100}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_address",
                down: "add_user_passphrase",
                up: "add_user_firstname",
                left: "add_user_phone_number",
              }),
            }}
            error={errorStates?.email}
            // autoFocus={medicine ? true : false}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_user_address"
            ref={addressRef}
            name="address"
            label="Address"
            value={formState?.address || ""}
            onFocus={() => setCurrentComponent("add_user_address")}
            onChange={(e) => {
              handleFormChange("address", e.target.value);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_password",
                down: "add_user_role",
                up: "add_user_lastname",
                left: "add_user_email",
              }),
            }}
            error={errorStates?.lastName}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ p: "25px 30px" }}>
        <Grid item xs={4}>
          <TextInput
            id="add_user_password"
            ref={passwordRef}
            type="password"
            name="password"
            label="Password"
            required={true}
            onFocus={() => setCurrentComponent("add_user_password")}
            onChange={(e) => {
              handleFormChange("password", e.target.value.replace(/\s/g, ""));
              if (errorStates?.password)
                setErrorStates((prev) => {
                  return { ...prev, password: false };
                });
            }}
            value={formState?.password || ""}
            onBlur={() => validateForm("password", formState)}
            maxInputLength={100}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_passphrase",
                down: "add_user_status",
                up: "add_user_phone_number",
                left: "add_user_address",
              }),
            }}
            error={errorStates?.password}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            id="add_user_passphrase"
            ref={passPhraseRef}
            name="passPhrase"
            label="Pass Phrase"
            type="password"
            value={formState?.passPhrase || ""}
            onFocus={() => setCurrentComponent("add_user_passphrase")}
            onChange={(e) => {
              // setFormState({ ...formState, passPhrase: e.target.value });
              handleFormChange("passPhrase", e.target.value);
              if (errorStates?.passPhrase)
                setErrorStates((prev) => {
                  return { ...prev, passPhrase: false };
                });
            }}
            onBlur={() => validateForm("passPhrase", formState)}
            maxInputLength={5000}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_role",
                down: "add_user_joining_date",
                up: "add_user_email",
                left: "add_user_password",
              }),
            }}
            error={errorStates?.passPhrase}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            fullWidth={false}
            label={"Role"}
            id="add_user_role"
            ref={roleRef}
            placeHolder="Role"
            openOnFocus={true}
            name="role"
            onFocus={() => {
              setCurrentComponent("add_user_role");
            }}
            onChange={(event, option) => {
              handleFormChange("role", null, true, option, true);
              setRole(option);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => option.label}
            options={roles}
            value={role || null}
            required={true}
            markers={JSON.stringify({
              up: "add_user_address",
              left: "add_user_passphrase",
              right: "add_user_status",
              down: "add_user_release_date",
            })}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            fullWidth={false}
            label="Status"
            id="add_user_status"
            ref={statusRef}
            placeHolder="Status"
            openOnFocus={true}
            name="status"
            onFocus={() => {
              setCurrentComponent("add_user_status");
            }}
            onChange={(event, option) => {
              handleFormChange("status", null, true, option, true);
              setStatus(option);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => option.label}
            getOptionDisabled={(option) => !!option.disabled}
            options={statusOptions}
            value={status}
            required={true}
            markers={JSON.stringify({
              up: "add_user_password",
              left: "add_user_role",
              right: !(
                status.id === "ACTIVE" || status.id === "PASSWORD_EXPIRED"
              )
                ? "add_user_reset_password_in_days"
                : "add_user_joining_date",
            })}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            id="add_user_joining_date"
            name="joiningDate"
            label="Joining Date"
            inputRef={joiningDateRef}
            onFocus={() => setCurrentComponent("add_user_joining_date")}
            onChange={(value) => {
              setJoiningDate(value);
              const formattedValue = value ? value.format("DD/MM/YYYY") : null;
              setFormState({ ...formState, joiningDate: formattedValue });
              handleFormChange("joiningDate", formattedValue);
              if (errorStates?.joiningDate) {
                setErrorStates((prev) => ({ ...prev, joiningDate: false }));
              }
            }}
            value={dayjs(formState.joiningDate, "DD/MM/YYYY")}
            inputProps={{
              "data-markers": JSON.stringify({
                left: "add_user_status",
                right: "add_user_release_date",
                down: "add_user_designation",
                up: "add_user_passphrase",
              }),
            }}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={4}>
          <DatePicker
            id="add_user_release_date"
            inputRef={ttlRef}
            name="ttl"
            label="Release Date"
            value={dayjs(formState.ttl, "DD/MM/YYYY")}
            disablePast={false}
            disabled={
              !(status.id === "ACTIVE" || status.id === "PASSWORD_EXPIRED")
            }
            onFocus={() => setCurrentComponent("add_user_release_date")}
            onChange={(newValue) => {
              if (formState.joiningDate === null) {
                displayToast({
                  type: "error",
                  title: "Please enter joining date first",
                });
                return;
              }
              const formattedValue = newValue
                ? newValue.format("DD/MM/YYYY")
                : null;

              setFormState({ ...formState, ttl: formattedValue });
              handleFormChange("ttl", formattedValue);
              if (errorStates?.ttl) {
                setErrorStates((prev) => ({ ...prev, ttl: false }));
              }
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_reset_password_in_days",
                down: "add_user_designation",
                up: "add_user_role",
                left: "add_user_joining_date",
              }),
            }}
            error={errorStates?.ttl}
            sx={{ width: "100%" }}
          />
          {formState.ttl && (
            <div
              style={{
                fontSize: "12px",
                fontWeight: "700",
              }}
            >
              {moment(deactivateDate, "DD/MM/YYYY").diff(moment(), "days") <
              0 ? (
                <span style={{ color: "red" }}>
                  User is deactivated since{" "}
                  {moment().diff(moment(deactivateDate, "DD/MM/YYYY"), "days")}{" "}
                  days
                </span>
              ) : (
                <span>
                  User will be deactivated in{" "}
                  {moment(deactivateDate, "DD/MM/YYYY").diff(moment(), "days")}{" "}
                  days
                </span>
              )}
            </div>
          )}
        </Grid>
        <Grid item xs={4}>
          <NumberInput
            id="add_user_reset_password_in_days"
            ref={resetPasswordInDaysRef}
            name="resetPasswordInDays"
            label="Reset Password(In Days)"
            value={formState.resetPasswordInDays || 60}
            allowNegative={false}
            onFocus={() =>
              setCurrentComponent("add_user_reset_password_in_days")
            }
            onValueChange={(values, e) => {
              handleFormChange("resetPasswordInDays", values.floatValue);
              if (errorStates?.resetPasswordInDays)
                setErrorStates((prev) => {
                  return { ...prev, resetPasswordInDays: false };
                });
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_designation",
                down: "add_user_branch",
                up: "add_user_status",
                left: !(
                  status.id === "ACTIVE" || status.id === "PASSWORD_EXPIRED"
                )
                  ? "add_user_status"
                  : "add_user_release_date",
              }),
            }}
            error={errorStates?.resetPasswordInDays}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            id="add_user_designation"
            name="designation"
            value={formState?.designation || ""}
            label="Designation"
            inputRef={designationRef}
            options={designationOption}
            onChange={(event, option) => {
              handleFormChange("designation", option);
            }}
            getOptionLabel={(option) => option}
            freeSolo
            onFocus={() => setCurrentComponent("add_user_designation")}
            onBlur={() => validateForm("add_user_designation", formState)}
            onInputChange={(e, value) => {
              if (!value) return handleFormChange("designation", null);
              const newValue = e.target.value;
              // To do
              // if (newValue?.length >= 3) {
              //   searchDesignations(`designationName=${newValue.trim()}`).then((resp) => {
              //     const updatedResp = resp.result.rows;
              //     setDesignationOption(updatedResp);
              //   });
              // } else {
              //   setDesignationOption([]);
              // }
              handleFormChange("designation", newValue);
            }}
            markers={JSON.stringify({
              right: "add_user_branch",
              up: "add_user_joining_date",
              down: "add_user_branch",
              left: "add_user_reset_password_in_days",
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <ChipInputSelect
            id="add_user_branch"
            value={branch || null}
            options={branches}
            groupBy={(option) => option.region}
            name="branch"
            required={true}
            ref={branchRef}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            onFocus={() => {
              setCurrentComponent("add_user_branch");
            }}
            onChange={(event, option) => {
              handleFormChange("branch", null, true, option);
              setBranch(option);
            }}
            openOnFocus={true}
            label="Branches"
            multiple={true}
            blurOnSelect={false}
            disableCloseOnSelect={true}
            renderGroup={(params) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    padding: "5px",
                    gap: "5px",
                  }}
                  key={params.key}
                >
                  <div
                    style={{
                      height: "30px",
                      width: "100%",
                      background: "#e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      padding: "5px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {params.group}
                  </div>
                  <div>{params.children}</div>
                </div>
              );
            }}
            renderOption={(props, option, { selected }) => {
              return (
                <div
                  {...props}
                  style={{
                    display: "flex",
                    height: "40px",
                    width: "100%",
                    padding: "1px 5px",
                    gap: "5px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItem: "center",
                    }}
                  >
                    <Checkbox checked={selected} />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItem: "center",
                      gap: "5px",
                    }}
                  >
                    <div
                      style={{
                        height: "15px",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      {option.name} ({option.code})
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option.address}
                    </div>
                  </div>
                </div>
              );
            }}
            markers={JSON.stringify({
              up: "add_user_reset_password_in_days",
              left: "add_user_designation",
              right: "add_user_username",
              down: "add_user_branch",
            })}
          />
        </Grid>
      </Grid>
      <div className={classes["add-user-save-button-wrapper"]}>
        <Button
          id="medicine_add_batch_save"
          ref={saveUserRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save User"
          color="violet"
          onClick={updateUserHandler}
        />
      </div>
    </div>
  );
};

export default AddUsers;
